import {
  Alert,
  alertTypes,
  alertVariants,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import { SYSTEM_ADMIN } from "constants/routes";
import { USERNAME } from "constants/systemAdmin";
import { modalTypes } from "constants/types";
import AddUserModalUser from "layout/systemAdmin/workspaces/modals/AddUserModalUser";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { hideModal } from "slices/app/actions/modals";
import { addUsersToWorkspace } from "slices/systemAdmin/actions/systemAdmin";
import { addUsersToWorkspace as userAdminAddUsersToWorkspace } from "slices/userAdmin/actions/userAdmin";
import { sortObjectArrayComparator } from "utils";

const workspaceSelector = (state) =>
  window.location.hash.includes("/system-admin")
    ? state.systemAdmin.workspacesInfo.workspaces
    : state.userAdmin.workspaceInfo.workspaces;

const usersSelector = (state) =>
  window.location.hash.includes("/system-admin")
    ? state.systemAdmin.usersInfo.users
    : state.userAdmin.usersInfo.users;

const AddUserModal = ({ workspaceId, currentLicensedUsers }) => {
  const dispatch = useDispatch();
  const workspaces = useSelector(workspaceSelector);
  const users = useSelector(usersSelector);
  const error = useSelector((state) => state.error?.errorMessage);
  const [search, setSearch] = useState("");
  const [userList, setUserList] = useState([]);
  const [checkedUsers, setCheckedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    let uniqueUsers = [...users];
    // users in workspaceId
    const workspaceUsers = workspaces
      ?.find((s) => s.id === workspaceId)
      .users.map((u) => u.username);
    // filter users already in that workspace
    uniqueUsers = uniqueUsers.filter(
      (uu) => !workspaceUsers.includes(uu.username)
    );
    uniqueUsers = uniqueUsers.sort(sortObjectArrayComparator(true, USERNAME));
    setUserList(uniqueUsers);
  }, []);

  useEffect(() => {
    setFilteredUsers([...new Set(userList.map((u) => u.username))]);
  }, [userList]);

  useEffect(() => {
    const filtered = [...new Set(userList.map((u) => u.username))];
    setFilteredUsers(filtered.filter((u) => u.includes(search)));
  }, [search]);

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.ADD_USER_TO_WORKSPACE));
  };

  const handleCheckChange = (user) => {
    let checked = checkedUsers;
    checked = checked.find((u) => u === user)
      ? checked.filter((u) => u !== user)
      : [...checked, user];
    setCheckedUsers(checked);
    setCheckedUsers(checked);
  };

  const handleAdd = () => {
    if (window.location.hash.includes("/system-admin")) {
      dispatch(
        addUsersToWorkspace(checkedUsers, currentLicensedUsers, workspaceId)
      );
    } else {
      dispatch(
        userAdminAddUsersToWorkspace(checkedUsers, currentLicensedUsers)
      );
    }
  };

  const emptyUsers = () => (
    <div
      className="add__user__modal__users__container__no_users"
      data-testid="empty-users"
    >
      {search !== "" ? (
        <span>No users match the filter</span>
      ) : (
        <span>
          No users available. Please <Link to={SYSTEM_ADMIN}>ADD</Link> users.
        </span>
      )}
    </div>
  );

  const modalBody = () => (
    <div className="add__user__modal">
      <input
        className="add__user__modal__search"
        placeholder="Type to filter"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="add__user__modal__users">
        <p className="add__user__modal__users__title">
          <span>
            <b className="intermedium">Select users from the list</b>
          </span>
        </p>
        <p className="add__user__modal__users__subtitle">
          <span className="intermedium">
            Click checkbox to add multiple users
          </span>
        </p>
        <div className="add__user__modal__users__container">
          {filteredUsers.length
            ? filteredUsers.map((u) => (
                <AddUserModalUser
                  username={u}
                  checkedUsers={checkedUsers}
                  handleChange={handleCheckChange}
                  hasMultipleWorkspaces={
                    userList.find((user) => user.username === u).workspaces
                      ?.length > 1
                  }
                />
              ))
            : emptyUsers()}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      data-test="addUserModal"
      className="fade-in"
      show
      onDismiss={handleCancel}
      backdrop
      centered
    >
      <Modal.Header>
        <b>Add Member(s)</b>
      </Modal.Header>
      <Modal.Body>
        {error !== undefined ? (
          <Alert
            variant={alertVariants.warning}
            text={error}
            type={alertTypes.inline}
            hideCloseButton
          />
        ) : null}
        {modalBody()}
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cancelButton"
          variant={NewButtonVariant.l2}
          onClick={handleCancel}
        >
          Cancel
        </NewButton>

        <NewButton
          data-testid="saveButton"
          variant={NewButtonVariant.l1}
          onClick={() => {
            handleAdd();
          }}
          disabled={!checkedUsers.length}
        >
          Add Member(s)
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AddUserModal;

AddUserModal.propTypes = {
  workspaceId: PropTypes.number.isRequired,
  currentLicensedUsers: PropTypes.number.isRequired,
};
